/**
 * Shared stock reorder / coverage policy.
 * Used by Rapport (instantCache) and reorder notifications — one formula only.
 */

/**
 * @param {string} [conservationType]
 * @returns {{ coverageTarget: number, reorderThreshold: number, transferThreshold: number }}
 */
export function getCoveragePolicy(conservationType) {
  const type = conservationType || 'NORMAL';

  if (type === 'PERISSABLE' || type === 'COURTE') {
    return { coverageTarget: 15, reorderThreshold: 7, transferThreshold: 3 };
  }
  if (type === 'MOYENNE') {
    return { coverageTarget: 30, reorderThreshold: 15, transferThreshold: 7 };
  }
  if (type === 'LONGUE' || type === 'SANS_DATE') {
    return { coverageTarget: 60, reorderThreshold: 30, transferThreshold: 15 };
  }
  // NORMAL / default
  return { coverageTarget: 30, reorderThreshold: 15, transferThreshold: 7 };
}

/**
 * Days of cover at Principal for a given daily sales average.
 * @param {number} principalStock
 * @param {number} avgDailySales
 * @returns {number}
 */
export function computePrincipalCoverage(principalStock, avgDailySales) {
  const stock = Number(principalStock) || 0;
  const avg = Number(avgDailySales) || 0;
  if (!(stock > 0 && avg > 0)) return 0;
  return stock / avg;
}

/**
 * Whether Principal needs a reorder suggestion.
 * @param {number} principalStock
 * @param {number} avgDailySales
 * @param {number} reorderThreshold days-of-cover alert line
 * @returns {boolean}
 */
export function needsReorderSuggestion(principalStock, avgDailySales, reorderThreshold) {
  const stock = Number(principalStock) || 0;
  const avg = Number(avgDailySales) || 0;
  if (!(avg > 0)) return false;
  const coverage = computePrincipalCoverage(stock, avg);
  return coverage < Number(reorderThreshold) || stock === 0;
}

/**
 * Net qty to order to reach coverageTarget days (current stock deducted).
 * @returns {number} integer qty, or 0 when no reorder needed
 */
export function computeSuggestedReorderQty(
  avgDailySales,
  principalStock,
  coverageTarget,
  reorderThreshold
) {
  const avg = Number(avgDailySales) || 0;
  const stock = Number(principalStock) || 0;
  const target = Number(coverageTarget) || 0;
  if (!needsReorderSuggestion(stock, avg, reorderThreshold) || !(avg > 0) || !(target > 0)) {
    return 0;
  }
  return Math.max(0, Math.ceil(avg * target - stock));
}

/**
 * Stock level that triggers a reorder alert (avg × reorderThreshold days).
 * Used as notification reorderPoint / cleanup threshold.
 * @returns {number}
 */
export function computeReorderPoint(avgDailySales, reorderThreshold) {
  const avg = Number(avgDailySales) || 0;
  const threshold = Number(reorderThreshold) || 0;
  if (!(avg > 0 && threshold > 0)) return 0;
  return avg * threshold;
}
